<template lang='pug'>
v-container
  v-row(
    v-if='!isNull'
  )
    v-col
      v-alert(
        v-if='isAnonymous'
        type='warning'
        icon='mdi-incognito'
      )
        | Signed in as Guest
      v-alert(
        v-if='!isAnonymous'
        type='success'
        icon='mdi-account'
      )
        | Signed in as {{ displayName }}
      v-btn(
        v-if='!isAnonymous'
        @click='signInAnonymously'
        block
        depressed
        outlined
      )
        | Sign out
  v-row(
    v-if='isAnonymous || isNull'
  )
    v-col
      v-btn(
        @click='signInWithPopup'
        block
        depressed
        outlined
      )
        | Continue with Google
  v-row(
    v-if='isAnonymous || isNull'
    align='center'
  )
    v-col
      v-divider
    v-col(
      cols='auto'
    )
      | or
    v-col
      v-divider
  v-row(
    v-if='isAnonymous || isNull'
  )
    v-col
      v-tabs(
        v-model='tab'
      )
        v-tab
          | Create account
        v-tab.log-in
          | Log in
  v-row(
    v-if='isAnonymous || isNull'
  )
    v-col
      EventToProp(
        v-slot='{ prop: isValid, updateProp: isValidUpdate }'
      )
        EventToProp(
          v-slot='{ prop: shouldShowPassword, updateProp: shouldShowPasswordUpdate }'
        )
          v-form(
            ref='form'
            @input='isValidUpdate'
          )
            label(
              for='email'
            )
              | Email
            v-text-field(
              v-model='email'
              type='email'
              id='email'
              outlined
              autocomplete='email'
              required
              :rules='[v => !!v || "Required"]'
            )
            label(
              for='password'
            )
              | Password
            v-text-field(
              v-model='password'
              :type='shouldShowPassword ? "text" : "password"'
              id='password'
              outlined
              :autocomplete='tab === 0 ? "new-password" : "current-password"'
              required
              :rules='[v => !!v || "Required"]'
              :append-icon='shouldShowPassword ? "mdi-eye-off" : "mdi-eye"'
              @click:append='shouldShowPasswordUpdate(!shouldShowPassword)'
            )
            v-btn(
              v-if='tab === 0'
              @click='signUp'
              :disabled='!isValid'
              block
              depressed
              outlined
            )
              | Sign up - It's free!
            v-btn.log-in(
              v-if='tab === 1'
              @click='signIn'
              :disabled='!isValid'
              block
              depressed
              outlined
            )
              | Log in
  v-row(
    v-if='isAnonymous || isNull'
  )
    v-col
      Dialog2
        template(
          v-slot:dialog
        )
          v-container
            v-row
              v-spacer
              v-col(
                cols='12'
                md='6'
                lg='4'
              )
                EventToProp(
                  v-slot='{ prop: isValid, updateProp: isValidUpdate }'
                )
                  v-form(
                    ref='form'
                    @input='isValidUpdate'
                  )
                    label(
                      for='emailReset'
                    )
                      | Email
                    v-text-field(
                      v-model='email'
                      type='email'
                      id='emailReset'
                      outlined
                      autocomplete='email'
                      required
                      :rules='[v => !!v || "Required"]'
                    )
                    v-btn(
                      @click='sendPasswordResetEmail'
                      :disabled='!isValid'
                      block
                      depressed
                      outlined
                    )
                      | Reset password
              v-spacer
            v-row(
              v-if='shouldShowPasswordResetAlert'
            )
              v-spacer
              v-col(
                cols='12'
                md='6'
                lg='4'
              )
                v-alert(
                  type='success'
                )
                  | Password reset email sent
              v-spacer
        template(
          v-slot:default='{ shouldShowDialogUpdate }'
        )
          v-btn(
            @click='shouldShowDialogUpdate(true)'
            block
            text
            x-small
          )
            | Forgot password?
  v-row(
    v-if='errorMessage'
  )
    v-col
      v-alert(
        type='error'
      )
        | {{ errorMessage }}
</template>

<script>
import { cond, get, isBoolean, negate, noop, pipe, replace } from 'lodash/fp';

import {
  signInAnonymously,
} from '../functions/signInAnonymously';

import {
  createUserWithEmailAndPassword,
} from '../functions/createUserWithEmailAndPassword';

import {
  signInWithEmailAndPassword,
} from '../functions/signInWithEmailAndPassword';

import {
  sendPasswordResetEmail as _sendPasswordResetEmail,
} from '../functions/sendPasswordResetEmail';

import {
  signInWithRedirect as _signInWithRedirect,
} from '../functions/signInWithRedirect';

import {
  app,
} from '../functions/initializeApp';

import {
  getAuth,
  onAuthStateChanged,
  // FacebookAuthProvider,
  GoogleAuthProvider,
  signInWithPopup as _signInWithPopup,
} from 'firebase/auth';

import Dialog2 from './Dialog2.vue';
import EventToProp from './EventToProp.vue';

const signInWithPopup = function ()
{
  _signInWithPopup(getAuth(app), new GoogleAuthProvider()).catch(noop);
};

const signUp = function ()
{
  this.error = null;

  createUserWithEmailAndPassword
  (this.email)
  (this.password)
  .catch(error => this.error = error);
};

const signIn = function ()
{
  this.error = null;

  signInWithEmailAndPassword
  (this.email)
  (this.password)
  .catch(error => this.error = error);
};

const sendPasswordResetEmail = function ()
{
  _sendPasswordResetEmail(this.email)
  .then(() => this.shouldShowPasswordResetAlert = true)
  .catch(noop);
};

const signInWithRedirect = function ()
{
  // const provider = new FacebookAuthProvider();
  const provider = new GoogleAuthProvider();

  _signInWithRedirect(provider);
};

const errorMessage = function ()
{
  return pipe
  ([
    replace
    ('Firebase: Error (auth/email-already-in-use).')
    ('Email already in use.'),
    replace
    ('Firebase: Error (auth/invalid-email).')
    ('Invalid email.'),
    replace
    ('Firebase: Error (auth/user-not-found).')
    ('User not found.'),
    replace
    ('Firebase: Error (auth/wrong-password).')
    ('Wrong password.'),
    replace
    ('Firebase: Password should be at least 6 characters (auth/weak-password).')
    ('Password should be at least 6 characters.'),
    replace
    ('Firebase: Error (auth/invalid-credential).')
    ('Invalid credential.'),
  ])
  (this?.error?.message);
};

const isAnonymous = function ()
{
  return this?.user?.isAnonymous;
};

const isNull = function ()
{
  return negate
  (isBoolean)
  (this?.isAnonymous);
};

const displayName = function ()
{
  return cond
  ([
    [get('user.displayName'), get('user.displayName')],
    [get('user.email'), get('user.email')],
  ])
  (this);
};

const mounted = function ()
{
  const auth = getAuth(app);

  this.unsubscribe = onAuthStateChanged
  (
    auth,
    user => this.user = user
  );
};

const destroyed = function ()
{
  this.unsubscribe();
};

export default
{
  components:
  {
    Dialog2,
    EventToProp,
  },
  computed:
  {
    displayName,
    errorMessage,
    isAnonymous,
    isNull,
  },
  mounted,
  data()
  {
    return {
      email: null,
      error: null,
      password: null,
      shouldShowPasswordResetAlert: false,
      unsubscribe: null,
      user: null,
      tab: 0,
    };
  },
  destroyed,
  methods:
  {
    sendPasswordResetEmail,
    signIn,
    signInAnonymously,
    signInWithPopup,
    signInWithRedirect,
    signUp,
  },
};
</script>